if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        // Re-resolve the add-on variant from the currently checked option pills
        // at the exact moment the main product is submitted.
        this.refreshGiftVariants();
        const giftVariantInputs = this.getGiftVariantInputs();
        const missingGiftVariantInput = giftVariantInputs.find(
          (input) => input.dataset.freeAddonRequired === 'true' && !input.disabled && !input.value
        );

        if (missingGiftVariantInput) {
          missingGiftVariantInput.setAttribute('aria-invalid', 'true');
          this.handleErrorMessage(
            missingGiftVariantInput.dataset.validationMessage || missingGiftVariantInput.validationMessage
          );
          const selectedGiftOption = missingGiftVariantInput
            .closest('free-addon-product')
            ?.querySelector('[data-free-addon-option]:checked');
          selectedGiftOption?.focus();
          return;
        }

        giftVariantInputs.forEach((input) => input.removeAttribute('aria-invalid'));

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const formData = new FormData(this.form);
        const giftVariantIds = this.getGiftVariantIds();

        // A bundle is sent as one Cart API request so the main product and every
        // configured gift are added together, without sequential-request delays.
        if (giftVariantIds.length) {
          const mainVariantId = formData.get('id');
          const quantity = Number(formData.get('quantity')) || 1;
          const mainItem = { id: mainVariantId, quantity };
          const properties = {};

          formData.forEach((value, key) => {
            const propertyMatch = key.match(/^properties\[(.*)\]$/);
            if (propertyMatch) properties[propertyMatch[1]] = value;
            if (key === 'selling_plan') mainItem.selling_plan = value;
          });

          if (Object.keys(properties).length) mainItem.properties = properties;

          config.body = JSON.stringify({
            items: [mainItem].concat(
              giftVariantIds
                .filter((giftVariantId) => String(giftVariantId) !== String(mainVariantId))
                .map((giftVariantId) => ({ id: giftVariantId, quantity }))
            ),
          });
        } else {
          delete config.headers['Content-Type'];
          config.body = formData;
        }

        if (this.cart) {
          const sections = this.cart.getSectionsToRender().map((section) => section.id);
          if (giftVariantIds.length) {
            const body = JSON.parse(config.body);
            body.sections = sections;
            body.sections_url = window.location.pathname;
            config.body = JSON.stringify(body);
          } else {
            formData.append('sections', sections);
            formData.append('sections_url', window.location.pathname);
          }
          this.cart.setActiveElement(document.activeElement);
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      getGiftPickers() {
        const productInfo = this.closest('product-info');
        const nearbyGiftPickers = productInfo
          ? [...productInfo.querySelectorAll('[data-free-addon-picker]')]
          : [...document.querySelectorAll('[data-free-addon-picker]')];
        const matchingGiftPickers = nearbyGiftPickers.filter(
          (picker) => picker.dataset.productFormId === this.form.id
        );

        return matchingGiftPickers.length ? matchingGiftPickers : nearbyGiftPickers;
      }

      refreshGiftVariants() {
        this.getGiftPickers().forEach((picker) => {
          picker.querySelectorAll('free-addon-product').forEach((giftProduct) => {
            if (giftProduct.initialized) giftProduct.updateVariant();
          });
        });
      }

      getGiftVariantInputs() {
        return this.getGiftPickers().flatMap((picker) => [
          ...picker.querySelectorAll('[data-free-addon-variant]'),
        ]);
      }

      getGiftVariantIds() {
        const selectedGiftVariantIds = this.getGiftVariantInputs()
          .filter((input) => !input.disabled)
          .map((input) => input.value)
          .filter((id) => id);

        return [...new Set(selectedGiftVariantIds)];
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

if (!customElements.get('free-addon-product')) {
  customElements.define(
    'free-addon-product',
    class FreeAddonProduct extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.variantInput = this.querySelector('[data-free-addon-variant]');
        this.status = this.querySelector('[data-free-addon-status]');
        const variantsJSON = this.querySelector('[data-free-addon-variants]');

        if (!this.variantInput || !variantsJSON) return;

        try {
          this.variants = JSON.parse(variantsJSON.textContent);
        } catch (error) {
          console.warn('Unable to read free add-on variants.', error);
          return;
        }

        this.initialized = true;
        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-free-addon-option]')) this.updateVariant();
        });
        this.updateVariant();
      }

      updateVariant() {
        const selectedOptions = [...this.querySelectorAll('[data-free-addon-option]:checked')].map(
          (input) => input.value
        );
        const selectedVariant = this.variants.find(
          (variant) =>
            variant.options.length === selectedOptions.length &&
            variant.options.every((option, index) => option === selectedOptions[index])
        );
        const selectedVariantExists = Boolean(selectedVariant);

        this.variantInput.value = selectedVariantExists ? selectedVariant.id : '';
        this.dataset.selectedVariantId = this.variantInput.value;
        this.toggleAttribute('data-unavailable-selection', !selectedVariantExists);

        if (this.status) {
          this.status.hidden = selectedVariantExists;
          if (!selectedVariantExists) this.status.textContent = this.dataset.unavailableMessage;
        }
      }
    }
  );
}
